import {
  type CustomerAnalyticsMoodysReportDto,
  CustomerAnalyticsUnitMixConfigType,
} from '@zg-rentals/ts-customer-analytics-client';
import { Text, View } from '@react-pdf/renderer';
import { styles } from './pdfStyles';
import { MarketTrendsColumn, MarketTrendsPageWrapper, MarketTrendsTextHeader } from './MarketTrendsComponents';
import { getRowStyle } from './pdfImageUtils';
import { formatCurrency } from '../formatCurrency';

const performanceWidthPercent = 25;
const unitMixWidthPercent = 20;
const unitMixConfigTypeDisplayNames = new Map([
  [CustomerAnalyticsUnitMixConfigType.STUDIO, 'Studio'],
  [CustomerAnalyticsUnitMixConfigType.ONE_BEDROOM, '1 Bedroom'],
  [CustomerAnalyticsUnitMixConfigType.TWO_BEDROOM, '2 Bedroom'],
  [CustomerAnalyticsUnitMixConfigType.THREE_OR_MORE_BEDROOM, '3 Bedroom'],
]);

export const PerformanceByVintageUnitMixPage = ({
  report,
  showPerformanceByVintage,
  showUnitMix,
}: {
  report: CustomerAnalyticsMoodysReportDto;
  showPerformanceByVintage: boolean;
  showUnitMix: boolean;
}) => {
  const generateUnitMixRows = (type: CustomerAnalyticsUnitMixConfigType) => {
    return (
      <>
        <View style={styles.rowTextHighlight}>
          <MarketTrendsColumn width={100} textAlign="left">
            <Text>{unitMixConfigTypeDisplayNames.get(type)}</Text>
          </MarketTrendsColumn>
        </View>
        {report.unitMixByConfigType[type].map((data, i) => (
          <View style={getRowStyle(i)} key={type + data.areaName}>
            <MarketTrendsColumn width={unitMixWidthPercent} textAlign="left">
              <Text> {data.areaName}</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={unitMixWidthPercent}>
              <Text> {(data.inventoryPercent * 100).toFixed(1)}%</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={unitMixWidthPercent}>
              <Text> {data.averageUnitSize.toLocaleString()}</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={unitMixWidthPercent}>
              <Text> {formatCurrency(data.askingRentPerUnit)}</Text>
            </MarketTrendsColumn>
            <MarketTrendsColumn width={unitMixWidthPercent}>
              <Text> ${data.askingRentPerSf.toLocaleString()}</Text>
            </MarketTrendsColumn>
          </View>
        ))}
      </>
    );
  };

  return (
    <MarketTrendsPageWrapper quarter={report.dataAsOfQuarter} year={report.dataAsOfYear}>
      <View style={styles.containerBase}>
        {showPerformanceByVintage && (
          <>
            <MarketTrendsTextHeader headerText="Performance by Vintage" paddingBottom={20} />
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={performanceWidthPercent} textAlign="left">
                  <Text>Year Built</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={performanceWidthPercent}>
                  <Text>Asking Rent (Per Unit)</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={performanceWidthPercent}>
                  <Text>% Vacant</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={performanceWidthPercent}>
                  <Text>Inventory %</Text>
                </MarketTrendsColumn>
              </View>
              {report.performanceByVintageReport.map((data, i) => (
                <View style={getRowStyle(i)} key={data.yearBuiltRange}>
                  <MarketTrendsColumn width={performanceWidthPercent} textAlign="left">
                    <Text> {data.yearBuiltRange}</Text>
                  </MarketTrendsColumn>
                  <MarketTrendsColumn width={performanceWidthPercent}>
                    <Text> {data.askingRent ? formatCurrency(data.askingRent) : '-'}</Text>
                  </MarketTrendsColumn>
                  <MarketTrendsColumn width={performanceWidthPercent}>
                    <Text> {data.vacancyPercent ? (data.vacancyPercent * 100).toFixed(1) + '%' : '-'}</Text>
                  </MarketTrendsColumn>
                  <MarketTrendsColumn width={performanceWidthPercent}>
                    <Text> {((data.inventoryPercent || 0) * 100).toFixed(1)}%</Text>
                  </MarketTrendsColumn>
                </View>
              ))}
            </View>
          </>
        )}
        {showUnitMix && (
          <>
            <MarketTrendsTextHeader
              headerText="Unit Mix: Inventory and Current Rent"
              paddingBottom={20}
              paddingTop={20}
            />
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={unitMixWidthPercent}></MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent * 2} textAlign="center">
                  <Text>Inventory</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent * 2} textAlign="center">
                  <Text>Current Asking Rent</Text>
                </MarketTrendsColumn>
              </View>
              <View style={styles.tableHeader}>
                <MarketTrendsColumn width={unitMixWidthPercent}></MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent}>
                  <Text>Inventory</Text>
                  <Text style={styles.tableSubHeading}>%</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent}>
                  <Text>Avg. Unit Size</Text>
                  <Text style={styles.tableSubHeading}>in SF</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent}>
                  <Text>Asking Rent</Text>
                  <Text style={styles.tableSubHeading}>per Unit</Text>
                </MarketTrendsColumn>
                <MarketTrendsColumn width={unitMixWidthPercent}>
                  <Text>Asking Rent</Text>
                  <Text style={styles.tableSubHeading}>per SF</Text>
                </MarketTrendsColumn>
              </View>
              {generateUnitMixRows(CustomerAnalyticsUnitMixConfigType.STUDIO)}
              {generateUnitMixRows(CustomerAnalyticsUnitMixConfigType.ONE_BEDROOM)}
              {generateUnitMixRows(CustomerAnalyticsUnitMixConfigType.TWO_BEDROOM)}
              {generateUnitMixRows(CustomerAnalyticsUnitMixConfigType.THREE_OR_MORE_BEDROOM)}
            </View>
          </>
        )}
      </View>
    </MarketTrendsPageWrapper>
  );
};
